/***
 * Created on 2023/11/9
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/

import React, {useRef, useState} from 'react';
import {useTranslation} from "react-i18next";
import google_logo_svg from "../../../static/img/utils/logo-google.svg";
import apple_logo_svg from "../../../static/img/utils/logo-apple.svg";
import facebook_logo_svg from "../../../static/img/utils/logo-facebook.svg";
import weixin_svg from '../../../static/img/utils/logo-wechat-white.svg';
import {useMutation} from "@tanstack/react-query";
import {apiPost} from "../../../service/api";
import LinkPasswordDialog from "./link_password_dialog";
import CreateAccountDialog from "./create_account_dialog";
import {useBoundedStore} from 'common/Store';
import {SocialPlatformService} from 'common/ServiceCommon';

function SocialAccount({region, isAgreed, showCreateNotice=true, action='signin', setValues}) {
    const { t } = useTranslation();
    const [socialError, setSocialError] = useState();
    const [socialAccount, setSocialAccount] = useState({});
    const store = useBoundedStore();
    const [linkWithWeixin, setLinkWithWeixin] = useState(false);

    const handleSocialResponse = (resp, error=null, variables=null, context=null) => {
        if (resp?.error) {
            if (resp?.data?.code) {
                setSocialError(resp.data.code);
            }
        }
        else if (error) {
            if (error?.cause?.code) {
                setSocialError(error.cause.code);
                if (error?.cause?.code === 1050023) {
                    store.globalAlert.setAlert(t('@Unable to get your {platform} accounts email address. Please set email address and try again.@', {
                        platform: socialAccount?.platform,
                        interpolation: {
                            prefix: '{',
                            suffix: '}',
                            escapeValue: false
                        }
                    }), 'error', true);
                }
                else if (error?.cause?.code === 1050002) {
                    store.globalAlert.setAlert(t('@User already exist@'), 'error', true);
                }
                else if (error?.cause?.code === 1050012) {
                    store.globalAlert.setAlert(t('@Social account has been linked by others.@'), 'error', true);
                }
            }
            else {
                throw new error(error);
            }
        }
        else {
            if (action === 'signin') {
                location.href = `/login${location.search}`;
            }
            else {
                setLinkWithWeixin(true);
                setValues(prev => {
                    return {
                        ...prev,
                        social_access_token: socialAccount?.social_token
                    }
                });
            }
        }
    }

    const closeErrorModal = () => {
        setSocialError(null);
    }

    const doSocialMutation = useMutation({
        mutationFn: async (data) => await apiPost('/ajax/socialAction', data),
        onSuccess: data => {
            console.log(data);
            handleSocialResponse(data);
        },
        onError: (error, variables, context) => {
            console.log(error);
            handleSocialResponse(null, error, variables, context);
        }
    })

    const openFixedWindow = (url, callback, {width=640, height=480}) => {
        const y = window.top.outerHeight / 2 + window.top.screenY - ( height / 2);
        const x = window.top.outerWidth / 2 + window.top.screenX - ( width / 2);
        const popup = window.open(url, 'Social Signin Window', `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${width}, height=${height}, top=${y}, left=${x}`);
        const interval_id = setInterval(() => {
            callback(interval_id, popup);
        }, 500);
        // popup.addEventListener('beforeunload', () => {
        //     clearInterval(interval_id);
        // })
        return popup;
    }

    const doGoogleSignIn = async (e) => {
        e.preventDefault();
        const url = SocialPlatformService.generateGoogleSignInUrl();
        openFixedWindow(url, (interval_id, popup) => {
            try {
                const return_url = new URL(popup.location.href);
                if (return_url.pathname === '/oauth2callback') {
                    const state = return_url.searchParams.get('state');
                    const isValid = sessionStorage.getItem('goauth_state') === state;
                    sessionStorage.removeItem('goauth_state');
                    if (!isValid) {
                        popup.close();
                        clearInterval(interval_id);
                        store.globalAlert.setAlert(t('@Fail to verify your account with Google. Please sign up with other way.@'), 'error', true);
                        return;
                    }
                    // handle the code here.
                    const data = {
                        platform: 'google',
                        social_token: return_url.searchParams.get('code'),
                        action: 'signin',
                        is_mobile: false
                    };
                    setSocialAccount(data);
                    doSocialMutation.mutate(data);
                    popup.close();
                    clearInterval(interval_id);
                }
            } catch (err) {
                console.log(err)
            }
        }, {});
    }

    const doFacebookSignIn = (e) => {
        e.preventDefault();
        const url = SocialPlatformService.generateFacebookSigninUrl();
        const popup = openFixedWindow(url, (interval_id, popup) => {
            const return_url = new URL(popup.window.location.href);
            if (return_url.pathname === '/facebook_oauth2callback') {
                const state = return_url.searchParams.get('state');
                const isValid = sessionStorage.getItem('fb_oauth_state') === state;
                sessionStorage.removeItem('fb_oauth_state');
                if (!isValid) {
                    popup.close();
                    clearInterval(interval_id);
                    store.globalAlert.setAlert(t('@Your Facebook token is invalid, please sign up with Facebook again.@'), 'error', true);
                    return;
                }
                // handle the code here.
                const data = {
                    platform: 'facebook',
                    social_token: return_url.searchParams.get('code'),
                    action: 'signin',
                    is_mobile: false
                }
                setSocialAccount(data);
                doSocialMutation.mutate(data);
                popup.close();
                clearInterval(interval_id);
            }
        }, {})
    }

    const doAppleSignIn = (e) => {
        e.preventDefault();
        const url = SocialPlatformService.generateAppleIdSignInUrl();
        openFixedWindow(url.toString(), (interval_id, popup) => {
            try {
                const return_url = new URL(popup.window.location.href);
                if (return_url.pathname === '/oauth2callback') {
                    const state = return_url.searchParams.get('state');
                    const isValid = sessionStorage.getItem('apple_oauth_state') === state;
                    sessionStorage.removeItem('apple_oauth_state');
                    if (!isValid) {
                        popup.close();
                        clearInterval(interval_id);
                        store.globalAlert.setAlert(t('@Appleid Auth Code Revoked, try login again.@'), 'error', true);
                        return;
                    }
                    // handle the code here.
                    const data = {
                        platform: 'appleid',
                        social_token: return_url.searchParams.get('code'),
                        action: 'signin',
                        is_mobile: false
                    }
                    setSocialAccount(data);
                    doSocialMutation.mutate(data);
                    popup.close();
                    clearInterval(interval_id);
                }
            } catch (err) {
                console.log(err);
            }
        }, {});
    }

    const doWechatSignin = (e) => {
        e.preventDefault();
        const url = SocialPlatformService.generateWeixinSignInUrl();
        openFixedWindow(url.toString(), (interval_id, popup) => {
            try {
                const return_url = new URL(popup.window.location.href);
                if (return_url.pathname === '/oauth2callback') {
                    const state = return_url.searchParams.get('state');
                    const isValid = sessionStorage.getItem('woauth_state') === state;
                    sessionStorage.removeItem('woauth_state');
                    if (!isValid) {
                        popup.close();
                        clearInterval(interval_id);
                        store.globalAlert.setAlert(t('@Error@'), 'error', true);
                        return;
                    }
                    // handle the code here.
                    const data = {
                        platform: 'weixin',
                        social_token: return_url.searchParams.get('code'),
                        action: action,
                        is_mobile: false
                    }
                    setSocialAccount(data);
                    doSocialMutation.mutate(data);
                    popup.close();
                    clearInterval(interval_id);
                }
            } catch (err) {
                console.log(err);
            }
        }, {});
    }

    const doWeixinCheck = (e) => {
        // e.preventDefault();
        if (linkWithWeixin) {
            setLinkWithWeixin(false)
            setSocialAccount(null);
            setValues(prev => {
                return {
                    ...prev,
                    social_access_token: null
                }
            })
        }
        else {
            try {
                doWechatSignin(e);
            } catch (error) {
                setLinkWithWeixin(false)
                setSocialAccount(null);
            }
        }

    }

    return (
        <>
            {region !== 'CHINA' &&
                <>
                    <button className="q-btn q-btn--default q-btn--social !mb-24" onClick={doGoogleSignIn}>
                        <img src={google_logo_svg} />
                        <span>Google</span>
                    </button>
                    <button className="q-btn q-btn--default q-btn--social !mb-24" onClick={doFacebookSignIn}>
                        <img src={facebook_logo_svg} />
                        <span>Facebook</span>
                    </button>
                    <button className="q-btn q-btn--default q-btn--social" onClick={doAppleSignIn}>
                        <img src={apple_logo_svg} />
                        <span>Apple</span>
                    </button>
                </>
            }

            { region === 'CHINA' && action === 'signin' &&
                <button className="q-btn q-btn--primary--focus btn-medium btn-rounded-normal mt-16"
                        data-btn-signin="wechat" disabled={(region === 'CHINA' && !isAgreed)} onClick={doWechatSignin}>
                    <span className="ic-wechat"><img src={weixin_svg}/></span>
                    <span>使用微信账号登录</span>
                </button>
            }
            { region === 'CHINA' &&   action === 'check_linked' &&
                <div className="comp-checkbox">
                    <label className="q-checkbox" >
                        <input type="checkbox" name="checkbox-weichat" checked={linkWithWeixin} onChange={doWeixinCheck}/>
                        <span>{t('@Link my WeChat account for social login@')}</span>
                    </label>
                </div>
            }

            {socialError === 1050024 && <CreateAccountDialog showModal={socialError === 1050024} closeModal={closeErrorModal} socialAccount={socialAccount} metadata={''} action={doSocialMutation} isShowNotice={showCreateNotice}/>}
            {socialError === 1050025 && <LinkPasswordDialog showModal={socialError === 1050025} closeModal={closeErrorModal} socialAccount={socialAccount} metadata={''} action={doSocialMutation}/>}
        </>
    );
}

export default SocialAccount;